import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

const PENDING_SAVE_KEY = "thaigayu_pending_save_location";

/**
 * If the user clicked "Save location" while logged out we stashed the city
 * in localStorage. After they authenticate, persist it to their account so
 * the intent isn't lost.
 */
async function consumePendingSaveLocation(refresh) {
  let raw;
  try { raw = localStorage.getItem(PENDING_SAVE_KEY); } catch { return; }
  if (!raw) return;
  let loc;
  try { loc = JSON.parse(raw); } catch { localStorage.removeItem(PENDING_SAVE_KEY); return; }
  if (!loc?.name || loc.lat == null || loc.lng == null) {
    localStorage.removeItem(PENDING_SAVE_KEY);
    return;
  }
  try {
    await api.post("/me/locations", {
      name: loc.name, lat: loc.lat, lng: loc.lng,
      country: loc.country || "", timezone: loc.timezone,
    });
    await refresh();
    toast.success(`Saved ${loc.displayName || loc.name} to your account`);
  } catch {
    toast.error("Signed in, but couldn't save your location. Try the save button again.");
  } finally {
    try { localStorage.removeItem(PENDING_SAVE_KEY); } catch { /* ignore */ }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=signed-out, object=signed-in
  const [authOpen, setAuthOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("thaigayu_token", data.access_token);
    setUser(data);
    await consumePendingSaveLocation(refresh);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    if (data.access_token) localStorage.setItem("thaigayu_token", data.access_token);
    setUser(data);
    await consumePendingSaveLocation(refresh);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("thaigayu_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, login, register, logout, refresh, authOpen, setAuthOpen }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
